import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// @ts-ignore
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { Document, Page } from 'react-pdf';
import * as pdfjsLib from 'pdfjs-dist';
// Set up PDF.js worker using local file
try {
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/pdf.worker.min.mjs';
  console.log('PDF.js worker configured successfully');
} catch (error) {
  console.warn('Failed to set PDF.js worker source:', error);
}

// Check if PDF.js is working
const isPDFJSAvailable = () => {
  try {
    return typeof pdfjsLib.getDocument === 'function';
  } catch (error) {
    console.error('PDF.js not available:', error);
    return false;
  }
};
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Skeleton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Notes as NotesIcon,
  PictureAsPdf as PdfIcon,
  ContentCopy as DuplicateIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { jobService, Job, UpdateJobDto, JobType, Priority, DuplicateJobDto } from '../services/jobService';
import { customerService, Customer } from '../services/customerService';
import { businessService, Business } from '../services/businessService';
import { appointmentService, Appointment } from '../services/appointmentService';
import { appointmentTypeService, AppointmentType } from '../services/appointmentTypeService';
import { formService, Form, FormFieldType } from '../services/formService';
import { useAuth } from '../contexts/AuthContext';
import { UserDataFieldDto } from '../types/customUserDataFields';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { indexedDbService } from '../services/indexedDbService';
import { PDFService } from '../services/pdfService';
import { apiHealthService } from '../services/apiHealthService';
import {
  documentService,
  JobDocument,
  UploadConstraints,
  formatFileSize
} from '../services/documentService';
import BaseDialog from '../components/common/BaseDialog';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobForm from '../components/common/JobForm';
import JobDetailsCard from '../components/jobDetail/JobDetailsCard';
import AppointmentsList from '../components/jobDetail/AppointmentsList';
import JobEditDialog from '../components/jobDetail/JobEditDialog';
import DuplicateJobDialog from '../components/jobDetail/DuplicateJobDialog';
import DocumentGallery from '../components/jobDetail/DocumentGallery';
import FormRenderer from '../components/forms/FormRenderer';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: '16px' }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

// PDF Viewer Component for Lightbox

// PDF Page Component for handling individual pages
const PDFPageComponent: React.FC<{
  pdfDoc: any;
  pageNumber: number;
  scale: number;
  currentFieldValues: { [key: string]: any };
  onFieldChange: (name: string, value: any) => void;
  onFieldsFound: (values: { [key: string]: any }) => void;
  form?: Form;
  userDataFields?: UserDataFieldDto[];
}> = ({ pdfDoc, pageNumber, scale, currentFieldValues, onFieldChange, onFieldsFound, form, userDataFields }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<any>(null);
  const isRenderingRef = useRef(false);
  const pageKeyRef = useRef(`${pdfDoc?.numPages}-${pageNumber}-${scale}`);

  useEffect(() => {
    console.log(`PDFPageComponent: Mounting page ${pageNumber} with scale ${scale}`);
    const renderPage = async () => {
      if (!canvasRef.current || !wrapperRef.current || !pdfDoc) return;

      // Prevent multiple simultaneous renders
      if (isRenderingRef.current) {
        console.log(`PDFPageComponent: Already rendering page ${pageNumber}, skipping`);
        return;
      }

      const currentPageKey = `${pdfDoc?.numPages}-${pageNumber}-${scale}`;
      pageKeyRef.current = currentPageKey;

      try {
        isRenderingRef.current = true;

        if (renderTaskRef.current) {
          try {
            console.log(`PDFPageComponent: Cancelling previous render for page ${pageNumber}`);
            await renderTaskRef.current.cancel();
          } catch (e) {
            // Expected - cancellation throws RenderingCancelledException
          }
          renderTaskRef.current = null;
        }

        // Small delay to ensure canvas is fully released
        await new Promise(resolve => setTimeout(resolve, 50));

        // Verify canvas is still valid and attached
        const canvas = canvasRef.current;
        if (!canvas || !canvas.isConnected) {
          console.log(`PDFPageComponent: Canvas is no longer connected, skipping render`);
          return;
        }

        const context = canvas.getContext('2d');
        if (!context) return;

        console.log(`PDFPageComponent: Rendering page ${pageNumber} at scale ${scale}`);
        const page = await pdfDoc.getPage(pageNumber);

        // Verify that page key hasn't changed (we still want to render this page)
        if (pageKeyRef.current !== currentPageKey) {
          console.log(`PDFPageComponent: Page key changed, aborting render`);
          return;
        }

        const viewport = page.getViewport({ scale });

        // Verify canvas is still valid
        if (!canvasRef.current || !canvasRef.current.isConnected) {
          console.log(`PDFPageComponent: Canvas disconnected during render`);
          return;
        }

        canvas.height = viewport.height;
        canvas.width = viewport.width;
        wrapperRef.current.style.width = `${viewport.width}px`;
        wrapperRef.current.style.height = `${viewport.height}px`;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        // Annotations
        const annotations = await page.getAnnotations();
        const formFields = annotations.filter((a: any) => a.subtype === 'Widget');

        // Remove old layers
        const oldLayers = wrapperRef.current.querySelectorAll('.annotationLayer');
        oldLayers.forEach(l => l.remove());

        const annotationLayer = document.createElement('div');
        annotationLayer.className = 'annotationLayer';
        annotationLayer.style.position = 'absolute';
        annotationLayer.style.left = '0';
        annotationLayer.style.top = '0';
        annotationLayer.style.width = `${viewport.width}px`;
        annotationLayer.style.height = `${viewport.height}px`;
        wrapperRef.current.appendChild(annotationLayer);

        const pageInitialValues: { [key: string]: any } = {};

        formFields.forEach((field: any, index: number) => {
          createInteractiveField(field, annotationLayer, index, pageInitialValues);
        });

        // Notify parent of found fields (defaults)
        onFieldsFound(pageInitialValues);

        console.log(`PDFPageComponent: Successfully rendered page ${pageNumber} at scale ${scale}`);
      } catch (error: any) {
        if (error.name !== 'RenderingCancelledException') {
          console.error('Error rendering page ' + pageNumber, error);
        }
      } finally {
        isRenderingRef.current = false;
      }
    };

    renderPage();

    return () => {
      console.log(`PDFPageComponent: Unmounting page ${pageNumber}`);
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
      isRenderingRef.current = false;
      // Clear the canvas to prevent reuse issues
      if (canvasRef.current) {
        const context = canvasRef.current.getContext('2d');
        if (context) {
          context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
    };
  }, [pdfDoc, pageNumber, scale]);

  // Update field values in DOM when currentFieldValues changes without re-rendering the whole page
  useEffect(() => {
    if (!wrapperRef.current) return;

    // Find all interactive elements with data-field-name
    const elements = wrapperRef.current.querySelectorAll('[data-field-name]');
    elements.forEach(el => {
      const fieldName = el.getAttribute('data-field-name');
      if (!fieldName) return;

      const newValue = currentFieldValues[fieldName];
      if (newValue === undefined) return;

      if (el instanceof HTMLInputElement) {
        if (el.type === 'checkbox') {
          const isChecked = Boolean(newValue);
          if (el.checked !== isChecked) {
            el.checked = isChecked;
            // Also find and update the checkmark sibling if it exists
            const checkmark = el.parentElement?.querySelector('div');
            if (checkmark) {
              checkmark.style.display = isChecked ? 'block' : 'none';
            }
          }
        } else {
          // Date, datetime or text
          if (el.type === 'date' || el.type === 'datetime-local') {
            // Need to normalize date for input value
            const normalized = normalizeDateForInput(newValue, el.type === 'date' ? 'date' : 'datetime');
            if (el.value !== normalized) {
              el.value = normalized;
            }
          } else {
            if (el.value !== String(newValue)) {
              el.value = String(newValue);
            }
          }
        }
      } else if (el instanceof HTMLTextAreaElement) {
        if (el.value !== String(newValue)) {
          el.value = String(newValue);
        }
      }
    });
  }, [currentFieldValues]);

  // Helper to normalize date for input values
  const normalizeDateForInput = (value: string, type: 'date' | 'datetime') => {
    if (!value) return '';
    // If it's dd/MM/yyyy
    const ddmmyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (ddmmyyyy) {
      return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
    }
    // If it's dd/MM/yyyy HH:mm
    const ddmmyyyyhhmm = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{1,2})$/.exec(value);
    if (ddmmyyyyhhmm) {
      const datePart = `${ddmmyyyyhhmm[3]}-${ddmmyyyyhhmm[2].padStart(2, '0')}-${ddmmyyyyhhmm[1].padStart(2, '0')}`;
      const timePart = `${ddmmyyyyhhmm[4].padStart(2, '0')}:${ddmmyyyyhhmm[5].padStart(2, '0')}`;
      return type === 'date' ? datePart : `${datePart}T${timePart}`;
    }

    try {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        const iso = d.toISOString();
        return type === 'date' ? iso.split('T')[0] : iso.substring(0, 16);
      }
    } catch (e) { }
    return value;
  };

  const createInteractiveField = (field: any, container: HTMLElement, index: number, initValuesAccumulator: any) => {
    const rect = field.rect;
    if (!rect || rect.length < 4) return;

    // PDF coordinates to viewport coordinates
    const [x1, y1, x2, y2] = rect;
    const width = (x2 - x1) * scale;
    const height = (y2 - y1) * scale;
    const left = x1 * scale;
    const top = (container.clientHeight - y2 * scale); // Flip Y coordinate

    // Determine initial value
    let initialValue: any = '';
    if (field.checkBox) {
      const raw = field.fieldValue || field.buttonValue;
      initialValue = raw === 'Yes' || raw === true || raw === 'On';
    } else {
      initialValue = field.fieldValue || field.buttonValue || '';
    }

    if (field.fieldName) {
      initValuesAccumulator[field.fieldName] = initialValue;
    }

    // Use current value from props if available (user edit), else PDF default
    const currentValue = field.fieldName && currentFieldValues[field.fieldName] !== undefined
      ? currentFieldValues[field.fieldName]
      : initialValue;

    if (field.checkBox) {
      const checkboxContainer = document.createElement('div');
      checkboxContainer.style.position = 'absolute';
      checkboxContainer.style.left = `${left}px`;
      checkboxContainer.style.top = `${top}px`;
      checkboxContainer.style.width = `${Math.min(width, 20)}px`;
      checkboxContainer.style.height = `${Math.min(height, 20)}px`;
      checkboxContainer.style.zIndex = '10';

      const checkmark = document.createElement('div');
      checkmark.textContent = '✓';
      checkmark.style.position = 'absolute';
      checkmark.style.top = '50%';
      checkmark.style.left = '50%';
      checkmark.style.transform = 'translate(-50%, -50%)';
      checkmark.style.fontSize = `${Math.min(height, 20) * 0.8}px`;
      checkmark.style.lineHeight = '1';
      checkmark.style.fontWeight = 'bold';
      checkmark.style.color = '#000';
      checkmark.style.pointerEvents = 'none';
      checkmark.style.userSelect = 'none';
      checkmark.style.display = currentValue ? 'block' : 'none';

      const checkboxInput = document.createElement('input');
      checkboxInput.type = 'checkbox';
      checkboxInput.checked = Boolean(currentValue);
      checkboxInput.style.position = 'absolute';
      checkboxInput.style.width = '100%';
      checkboxInput.style.height = '100%';
      checkboxInput.style.margin = '0';
      checkboxInput.style.padding = '0';
      checkboxInput.style.opacity = '0';
      checkboxInput.style.cursor = 'pointer';
      if (field.fieldName) {
        checkboxInput.setAttribute('data-field-name', field.fieldName);
      }

      checkboxInput.addEventListener('change', (e) => {
        const target = e.target as HTMLInputElement;
        checkmark.style.display = target.checked ? 'block' : 'none';
        if (field.fieldName) {
          onFieldChange(field.fieldName, target.checked);
        }
      });

      checkboxContainer.appendChild(checkmark);
      checkboxContainer.appendChild(checkboxInput);
      container.appendChild(checkboxContainer);

    } else {
      let inputElement: HTMLInputElement | HTMLTextAreaElement;

      // Determine field type from form metadata if available
      let fieldType: any;
      if (form && field.fieldName) {
        for (const section of form.formSections || []) {
          const found = section.formFields?.find(f => f.code === field.fieldName);
          if (found) {
            fieldType = found.fieldType;
            break;
          }
        }
      }

      const isDateField = fieldType === FormFieldType.Date || fieldType === 'Date' || fieldType === 7;
      const isDateTimeField = fieldType === FormFieldType.DateTime || fieldType === 'DateTime' || fieldType === 8;

      if (field.multiLine) {
        inputElement = document.createElement('textarea');
        inputElement.style.resize = 'none';
      } else if (isDateField || isDateTimeField) {
        inputElement = document.createElement('input');
        (inputElement as HTMLInputElement).type = isDateField ? 'date' : 'datetime-local';

        inputElement.value = normalizeDateForInput(currentValue || '', isDateField ? 'date' : 'datetime');
      } else {
        inputElement = document.createElement('input');
        (inputElement as HTMLInputElement).type = 'text';
      }

      if (field.fieldName) {
        inputElement.setAttribute('data-field-name', field.fieldName);
      }

      if (!(isDateField || isDateTimeField)) {
        inputElement.value = currentValue || '';
      }
      inputElement.style.width = `${width}px`;
      inputElement.style.height = `${height}px`;

      const fontSize = Math.min(height * (field.multiLine ? 0.6 : 0.7), 14);
      inputElement.style.fontSize = `${fontSize}px`; // Use simpler font size logic

      inputElement.style.position = 'absolute';
      inputElement.style.left = `${left}px`;
      inputElement.style.top = `${top}px`;
      inputElement.style.border = '1px solid #ccc';
      inputElement.style.backgroundColor = 'white';
      inputElement.style.zIndex = '10';
      inputElement.style.padding = '2px';
      inputElement.style.boxSizing = 'border-box';

      inputElement.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        if (field.fieldName) {
          onFieldChange(field.fieldName, target.value);
        }
      });

      container.appendChild(inputElement);
    }

    // Load Default Button Logic (Shared for both input and checkbox)
    let userDataProperty: string | null = null;
    if (form && field.fieldName) {
      for (const section of form.formSections || []) {
        const found = section.formFields?.find(f => f.code === field.fieldName);
        if (found) {
          try {
            const config = found.configurationJson ? JSON.parse(found.configurationJson) : {};
            if (config?.parameterValue?.source === 'UserDataField') {
              userDataProperty = config.parameterValue.property;
            }
          } catch (e) { }
          break;
        }
      }
    }

    if (userDataProperty && userDataFields) {
      const matchingVal = userDataFields.find((f: UserDataFieldDto) => f.businessDataField?.code === userDataProperty)?.value;
      if (matchingVal !== undefined && matchingVal !== null) {
        const loadBtn = document.createElement('button');
        loadBtn.innerHTML = 'Load Default';
        loadBtn.title = `Load Default: ${matchingVal}`;
        loadBtn.style.position = 'absolute';
        // Position 50px to the right of the field
        const btnLeft = field.checkBox ? left + Math.min(width, 20) + 50 : left + width + 50;
        loadBtn.style.left = `${btnLeft}px`;
        loadBtn.style.top = `${top}px`;
        loadBtn.style.height = '50px'; //`${Math.max(height, 20)}px`; // Ensure button is at least 20px high for text
        loadBtn.style.width = '80px'; // Wider for text
        loadBtn.style.padding = '0 4px';
        loadBtn.style.fontSize = '10px';
        loadBtn.style.fontWeight = 'bold';
        loadBtn.style.whiteSpace = 'nowrap';
        loadBtn.style.cursor = 'pointer';
        loadBtn.style.zIndex = '11';
        loadBtn.style.backgroundColor = '#f0f0f0';
        loadBtn.style.border = '1px solid #ccc';
        loadBtn.style.borderRadius = '3px';

        loadBtn.onclick = (e) => {
          e.preventDefault();
          if (field.fieldName) {
            // Handle boolean values for checkboxes
            let valToSet: any = matchingVal;
            if (field.checkBox) {
              valToSet = matchingVal === 'true' || matchingVal === 'True' || matchingVal === 'Yes' || matchingVal === 'On';
            }
            onFieldChange(field.fieldName, valToSet);
          }
        };
        container.appendChild(loadBtn);
      }
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', backgroundColor: 'white' }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
};

const PDF_ZOOM_STORAGE_KEY = 'pdf_viewer_zoom_level';

const InteractivePDFViewer: React.FC<{
  pdfBytes: Uint8Array | null;
  loading: boolean;
  onDownloadAndSave?: (formData: { [key: string]: any }, saveOnly?: boolean, print?: boolean) => void;
  onRetry?: () => void;
  form?: Form;
  userDataFields?: UserDataFieldDto[];
}> = ({ pdfBytes, loading, onDownloadAndSave, onRetry, form, userDataFields }) => {
  console.log('InteractivePDFViewer: Component render - pdfBytes:', !!pdfBytes, 'loading:', loading);

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [formFieldValues, setFormFieldValues] = useState<{ [key: string]: any }>({});
  const [error, setError] = useState<string | null>(null);
  const [scale, setScale] = useState(() => {
    const savedScale = localStorage.getItem(PDF_ZOOM_STORAGE_KEY);
    if (savedScale) {
      const val = parseFloat(savedScale);
      return isNaN(val) ? 1.0 : val;
    }
    return 1.0;
  });
  const [retryTrigger, setRetryTrigger] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Save zoom level when it changes
  useEffect(() => {
    localStorage.setItem(PDF_ZOOM_STORAGE_KEY, scale.toString());
  }, [scale]);

  const pdfDocRef = useRef<any>(null);
  const isLoadingRef = useRef(false);
  const lastPdfBytesRef = useRef<string>('');

  // Load PDF when bytes change
  useEffect(() => {
    if (!pdfBytes || loading || isLoadingRef.current) {
      return;
    }

    // Check if PDF bytes actually changed (compare as string to avoid comparing large arrays)
    const currentPdfBytesStr = Array.from(pdfBytes).join(',');
    if (currentPdfBytesStr === lastPdfBytesRef.current) {
      console.log('InteractivePDFViewer: PDF bytes unchanged, skipping reload');
      return;
    }

    const loadPDF = async () => {
      try {
        isLoadingRef.current = true;
        if (!isPDFJSAvailable()) {
          throw new Error('PDF.js is not available or not properly configured');
        }

        setIsReady(false);
        setError(null);
        setFormFieldValues({});
        setNumPages(0);

        // Destroy existing PDF document if it exists
        if (pdfDocRef.current) {
          try {
            pdfDocRef.current.destroy();
          } catch (e) {
            console.log('Error destroying PDF document:', e);
          }
          pdfDocRef.current = null;
        }

        const pdfData = pdfBytes.slice(0);
        const pdf = await pdfjsLib.getDocument({ data: pdfData }).promise;
        console.log('InteractivePDFViewer: PDF loaded, pages:', pdf.numPages);

        pdfDocRef.current = pdf;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setIsReady(true);
        lastPdfBytesRef.current = currentPdfBytesStr;

      } catch (error) {
        console.error('InteractivePDFViewer: Error loading PDF:', error);
        setError(`Failed to load PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadPDF();

    return () => {
      // Clean up PDF document when component unmounts or dependencies change
      if (pdfDocRef.current) {
        try {
          pdfDocRef.current.destroy();
        } catch (e) {
          // Ignore errors during cleanup
        }
        pdfDocRef.current = null;
      }
    };
  }, [pdfBytes, loading, retryTrigger]);

  const handleFieldChange = (name: string, value: any) => {
    setFormFieldValues(prev => ({ ...prev, [name]: value }));
  };

  const handleFieldsFound = (values: { [key: string]: any }) => {
    setFormFieldValues(prev => {
      // Only add values that don't exist yet to avoid overwriting user input
      const next = { ...prev };
      let changed = false;
      Object.entries(values).forEach(([k, v]) => {
        if (next[k] === undefined) {
          next[k] = v;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3.0));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.25));

  const handleDownload = () => {
    setIsPrinting(true);
    onDownloadAndSave?.(formFieldValues, false, false);
    setTimeout(() => setIsPrinting(false), 500);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    onDownloadAndSave?.(formFieldValues, false, true);
    setTimeout(() => setIsPrinting(false), 500);
  };

  const handleSave = () => onDownloadAndSave?.(formFieldValues, true, false);

  // Check if form has fields with UserDataField parameter source
  const hasUserDataFieldSources = () => {
    const hasSources = !!form?.formSections?.some(section =>
      section.formFields?.some(field => {
        try {
          const config = field.configurationJson ? JSON.parse(field.configurationJson) : {};
          return config?.parameterValue?.source === 'UserDataField';
        } catch (e) { return false; }
      })
    );
    return hasSources;
  };

  // Load default values from UserDataFields for fields with UserDataField parameter source
  const handleLoadDefaults = () => {
    console.log('🚀 handleLoadDefaults clicked!');
    console.log('Current form state:', { hasForm: !!form, hasSections: !!form?.formSections });
    console.log('UserDataFields:', userDataFields);
    if (!form?.formSections) {
      console.log('No form sections found');
      return;
    }
    if (!userDataFields || userDataFields.length === 0) {
      console.log('No userDataFields provided', userDataFields);
      return;
    }

    console.log('Processing fields... userDataFields:', userDataFields);

    const newValues: { [key: string]: any } = { ...formFieldValues };
    let hasChanges = false;

    for (const section of form.formSections) {
      if (!section.formFields) continue;

      for (const field of section.formFields) {
        try {
          const config = field.configurationJson ? JSON.parse(field.configurationJson) : {};

          if (config?.parameterValue?.source === 'UserDataField') {
            const property = config.parameterValue.property;
            console.log(`Checking field ${field.code} with property ${property}`);
            const userDataField = userDataFields.find((f: UserDataFieldDto) => f.businessDataField?.code === property);

            if (userDataField) {
              console.log(`Found match for ${field.code}:`, userDataField.value);
              if (userDataField.value !== undefined && userDataField.value !== null) {
                newValues[field.code] = userDataField.value;
                hasChanges = true;
              }
            } else {
              console.log(`No match found for property ${property} in userDataFields`);
            }
          }
        } catch (e) {
          console.error(`Error loading default for field ${field.code}:`, e);
        }
      }
    }

    if (hasChanges) {
      console.log('Applying changes:', newValues);
      setFormFieldValues(newValues);
    } else {
      console.log('No changes were made');
    }
  };

  const handleRetry = () => {
    if (onRetry) onRetry();
    else {
      setError(null);
      setPdfDoc(null);
      setRetryTrigger(prev => prev + 1);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '600px', width: '100%' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Generating PDF...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '600px', width: '100%', p: 2 }}>
        <Typography variant="h6" color="error" gutterBottom>PDF Loading Error</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{error}</Typography>
        <Button variant="outlined" onClick={handleRetry}>Retry Loading PDF</Button>
      </Box>
    );
  }

  if (!pdfBytes) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>PDF not available</Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', p: 2 }}>
      {/* Action Buttons */}
      <Box display="flex" gap={1} justifyContent="center" sx={{ mb: 2 }}>
        <IconButton onClick={handleZoomOut}><ZoomOutIcon /></IconButton>
        <Typography variant="body2" sx={{ alignSelf: 'center', mx: 1 }}>{Math.round(scale * 100)}%</Typography>
        <IconButton onClick={handleZoomIn}><ZoomInIcon /></IconButton>
        <Box sx={{ mx: 2, height: '24px', borderLeft: '1px solid #ccc' }} />
        <IconButton onClick={handleDownload} color="primary" title="Download PDF"><DownloadIcon /></IconButton>
        <IconButton onClick={handlePrint} color="primary" title="Print PDF"><PrintIcon /></IconButton>
        <IconButton onClick={handleSave} color="primary" title="Save Data"><SaveIcon /></IconButton>
      </Box>

      {/* Pages Container */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{
          flex: 1,
          overflow: 'auto',
          border: '1px solid #ccc',
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column', // Stack pages vertically
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          padding: '20px'
        }}>
          {isReady && pdfDoc && Array.from({ length: numPages }).map((_, i) => (
            <PDFPageComponent
              key={`${retryTrigger}-${i + 1}`}
              pdfDoc={pdfDoc}
              pageNumber={i + 1}
              scale={scale}
              currentFieldValues={formFieldValues}
              onFieldChange={handleFieldChange}
              onFieldsFound={handleFieldsFound}
              form={form}
              userDataFields={userDataFields}
            />
          ))}
          {!isReady && <CircularProgress />}
        </Box>
      </Box>

      {/* Debug: Show current form field values
      {Object.keys(formFieldValues).length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>Debug Values:</Typography>
          <Box component="pre" sx={{ fontSize: '12px', overflow: 'auto', maxHeight: '100px' }}>
            {JSON.stringify(formFieldValues, null, 2)}
          </Box>
        </Box>
      )} */}
    </Box>
  );
};

// Helper function to convert large byte arrays to base64 without exceeding call stack
const convertBytesToBase64 = async (bytes: Uint8Array): Promise<string> => {
  console.log('🔄 [PixelVersion Save] Converting bytes to base64, total size:', bytes.length, 'bytes');

  // Process in chunks to avoid call stack overflow
  const chunkSize = 8192; // 8KB chunks
  let result = '';
  const totalChunks = Math.ceil(bytes.length / chunkSize);

  console.log('🔄 [PixelVersion Save] Processing in', totalChunks, 'chunks of', chunkSize, 'bytes each');

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    const chunkIndex = Math.floor(i / chunkSize) + 1;
    console.log('🔄 [PixelVersion Save] Processing chunk', chunkIndex, '/', totalChunks, '- size:', chunk.length);
    result += String.fromCharCode.apply(null, Array.from(chunk));
  }

  console.log('🔄 [PixelVersion Save] All chunks processed, applying base64 encoding...');
  const base64 = btoa(result);
  console.log('🔄 [PixelVersion Save] Base64 encoding complete');

  return base64;
};

// Handle save only for PixelVersion PDFs (no download)
const handleSavePixelVersion = async (
  form: Form,
  job: any,
  customer: any,
  loadFormResponses: (formId: number) => Promise<any>,
  capturedFormData?: { [key: string]: any },
  userDataFields?: any[]
) => {
  console.log('🚀 [PixelVersion Save] Function called with form:', form.id, 'job:', job?.id);
  if (!job) {
    console.log('❌ [PixelVersion Save] No job provided, returning early');
    return;
  }

  try {
    // Merge with existing data to prevent wiping out fields not in the current PDF view
    const existingFormData = await loadFormResponses(form.id) || {};
    const formData = { ...existingFormData, ...(capturedFormData || {}) };

    // If we have data, save it to the API
    if (Object.keys(formData).length > 0) {
      console.log('💾 [PixelVersion Save] Saving merged form data to API, field count:', Object.keys(formData).length);
      console.log('💾 [PixelVersion Save] Form data keys:', Object.keys(formData));
      await formService.submitFormResponse(form, job.id, formData);
      console.log('✅ [PixelVersion Save] Form data saved successfully');
    } else {
      console.log('⚠️ [PixelVersion Save] No form data to save, skipping PDF generation');
      return;
    }

      // Generate and upload PDF to Azure blob storage
      try {
        console.log('🔄 [PixelVersion Save] Starting PDF generation and upload process...');
        console.log('🔄 [PixelVersion Save] Form ID:', form.id, 'Job ID:', job.id);

        // Fetch form parameter values for preloading
        console.log('🔄 [PixelVersion Save] Fetching form parameter values...');
        const formParameterValues = await formService.getFormFieldParameterValues(job.id, form.id);
        console.log('🔄 [PixelVersion Save] Parameter values received:', Object.keys(formParameterValues || {}).length, 'parameters');

        // Generate filename
        const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
        const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
        const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

        // Parse PDF configuration
        let pdfConfig: any = {};
        if (form.pdfConfigurationJson) {
          try {
            pdfConfig = JSON.parse(form.pdfConfigurationJson);
          } catch (error) {
            console.warn('Failed to parse PDF configuration JSON:', error);
          }
        }

        // Prepare header data
        const headerData: { [key: string]: string | number | boolean | null | undefined } = {
          DisplayName: job.assignedSalesPersonName || ''
        };

        // Generate PDF with merged data (existing + captured)
        console.log('🔄 [PixelVersion Save] Starting PDF generation...');
        console.log('🔄 [PixelVersion Save] Form data keys:', Object.keys(formData));
        console.log('🔄 [PixelVersion Save] Filename:', filename);
        console.log('🔄 [PixelVersion Save] Form sections count:', form.formSections?.length || 0);

        const pdfBytes = await PDFService.generateEditablePDF(
          formData,
          form.description,
          form.formSections || [],
          {
            filename,
            headerConfigurationJson: form.headerConfigurationJson,
            headerHeight: pdfConfig.headerHeight ?? 100,
            pageMargin: pdfConfig.pageMargin ?? 15,
            headerData,
            defaultFontSize: pdfConfig.defaultFontSize,
            labelFontSize: pdfConfig.labelFontSize,
            multilineFontSize: pdfConfig.multilineFontSize,
            sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
            fieldSpacing: pdfConfig.fieldSpacing,
            sectionSpacing: pdfConfig.sectionSpacing,
            checkboxListSpacing: pdfConfig.checkboxListSpacing,
            sectionPadding: pdfConfig.sectionPadding,
            fieldHeight: pdfConfig.fieldHeight,
            showSectionBorders: pdfConfig.showSectionBorders,
            checkboxListColumns: pdfConfig.checkboxListColumns,
            pixelVersion: true,
            job,
            customer,
            formParameterValues,
          userDataFields
          }
        );

        console.log('🔄 [PixelVersion Save] PDF generated successfully, size:', pdfBytes.length, 'bytes');

        // Convert PDF bytes to base64 for upload (handle large PDFs in chunks)
        console.log('🔄 [PixelVersion Save] Converting PDF to base64...');
        const base64Pdf = await convertBytesToBase64(pdfBytes);
        console.log('🔄 [PixelVersion Save] Base64 conversion complete, length:', base64Pdf.length);

        // Upload PDF to Azure blob storage
        console.log('🔄 [PixelVersion Save] Starting Azure blob storage upload...');
        console.log('🔄 [PixelVersion Save] Upload payload:', {
          jobId: job.id,
          formId: form.id,
          formName: form.description,
          jobNumber: job.jobNumber || '',
          pdfContentLength: base64Pdf.length
        });

        const uploadStartTime = Date.now();
        await documentService.storeFormPdf({
          jobId: job.id,
          formId: form.id,
          formName: form.description,
          jobNumber: job.jobNumber || '',
          pdfContent: base64Pdf
        });

        const uploadEndTime = Date.now();
        console.log('✅ [PixelVersion Save] PDF uploaded to Azure successfully in', uploadEndTime - uploadStartTime, 'ms');
      } catch (pdfError) {
        console.error('❌ [PixelVersion Save] Error generating/uploading PDF:', pdfError);
        console.error('❌ [PixelVersion Save] PDF error details:', {
          message: (pdfError as Error).message,
          stack: (pdfError as Error).stack,
          formId: form.id,
          jobId: job.id
        });
        // Don't throw - we still want the form data to be saved even if PDF upload fails
      }
  } catch (error) {
    console.error('❌ [PixelVersion Save] Error saving form data:', error);
    console.error('❌ [PixelVersion Save] Form save error details:', {
      message: (error as Error).message,
      stack: (error as Error).stack,
      formId: form?.id,
      jobId: job?.id
    });
    throw error;
  }
};

// Handle download and save for PixelVersion PDFs
const handleDownloadAndSavePixelVersion = async (
  form: Form,
  job: any,
  customer: any,
  loadFormResponses: (formId: number) => Promise<any>,
  capturedFormData?: { [key: string]: any },
  shouldPrint: boolean = false,
  userDataFields?: any[]
) => {
  if (!job) return;

  try {
    // Fetch form parameter values for preloading
    const formParameterValues = await formService.getFormFieldParameterValues(job.id, form.id);
    // Get current form responses and merge with captured data
    const existingFormData = await loadFormResponses(form.id) || {};
    const formData = { ...existingFormData, ...(capturedFormData || {}) };

    // If we have data, save it to the API first
    if (Object.keys(formData).length > 0) {
      console.log('Saving merged form data to API:', formData);
      try {
        await formService.submitFormResponse(form, job.id, formData);
        console.log('Form data saved successfully');
      } catch (saveError) {
        console.error('Error saving form data:', saveError);
        // Continue with PDF generation even if save fails
      }
    }

    // Generate filename
    const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
    const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

    // Parse PDF configuration
    let pdfConfig: any = {};
    if (form.pdfConfigurationJson) {
      try {
        pdfConfig = JSON.parse(form.pdfConfigurationJson);
      } catch (error) {
        console.warn('Failed to parse PDF configuration JSON:', error);
      }
    }

    // Prepare header data
    const headerData: { [key: string]: string | number | boolean | null | undefined } = {
      DisplayName: job.assignedSalesPersonName || ''
    };

    // Generate PDF with merged data (existing + captured)
    console.log("GENERATEEDITABLEPDF 1", form.formSections);
    const pdfBytes = await PDFService.generateEditablePDF(
      formData,
      form.description,
      form.formSections || [],
      {
        filename,
        headerConfigurationJson: form.headerConfigurationJson,
        headerHeight: pdfConfig.headerHeight ?? 100,
        pageMargin: pdfConfig.pageMargin ?? 15,
        headerData,
        defaultFontSize: pdfConfig.defaultFontSize,
        labelFontSize: pdfConfig.labelFontSize,
        multilineFontSize: pdfConfig.multilineFontSize,
        sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
        fieldSpacing: pdfConfig.fieldSpacing,
        sectionSpacing: pdfConfig.sectionSpacing,
        checkboxListSpacing: pdfConfig.checkboxListSpacing,
        sectionPadding: pdfConfig.sectionPadding,
        fieldHeight: pdfConfig.fieldHeight,
        showSectionBorders: pdfConfig.showSectionBorders,
        checkboxListColumns: pdfConfig.checkboxListColumns,
        pixelVersion: true,
        job,
        customer,
        formParameterValues,
        userDataFields
      }
    );

    // Create blob
    const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    if (shouldPrint) {
      // Create a hidden iframe for printing
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        // Give it some time to open the print dialog before cleaning up
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(url);
        }, 60000);
      };

      return 'Print dialog opened successfully with form data saved';
    } else {
      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up URL after download
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);

      return 'PDF downloaded successfully with form data saved';
    }

  } catch (err: any) {
    console.error('Error downloading PDF:', err);
    throw new Error(err.message || 'Failed to download PDF');
  }
};

// PDF Viewer for PixelVersion forms - displays interactive PDF with form field capture
const PixelVersionPDFViewer: React.FC<{
  pdfBytes: Uint8Array | null;
  loading: boolean;
  form: Form;
  job: any;
  customer: any;
  loadFormResponses: (formId: number) => Promise<any>;
  generatePixelVersionPdf: (form: Form) => Promise<void>;
  setSuccess: (message: string | null) => void;
  setError: (message: string | null) => void;
  setSkipPdfRegeneration: (skip: boolean) => void;
  onRetry?: () => void;
  userDataFields?: UserDataFieldDto[];
}> = ({
  pdfBytes,
  loading,
  form,
  job,
  customer,
  loadFormResponses,
  generatePixelVersionPdf,
  setSuccess,
  setError,
  setSkipPdfRegeneration,
  onRetry,
  userDataFields
}) => {
    const handleRetryFromParent = async () => {
      console.log('PixelVersionPDFViewer: Handling retry from child component');
      // Clear the current PDF bytes to force regeneration
      setError(null);
      // Regenerate the PDF
      await generatePixelVersionPdf(form);
    };

    return (
      <InteractivePDFViewer
        pdfBytes={pdfBytes}
        loading={loading}
        form={form}
        userDataFields={userDataFields}
        onDownloadAndSave={async (capturedFormData, saveOnly = false, shouldPrint = false) => {
          try {
            // Prevent automatic PDF regeneration during these operations
            setSkipPdfRegeneration(true);

            setError(null);
            if (saveOnly) {
              // Save only - no download or PDF regeneration
              await handleSavePixelVersion(form, job, customer, loadFormResponses, capturedFormData, userDataFields);
              setSuccess('Form data saved successfully');
            } else {
              // Download/Print and save
              const successMessage = await handleDownloadAndSavePixelVersion(
                form,
                job,
                customer,
                loadFormResponses,
                capturedFormData,
                shouldPrint,
                userDataFields
              );
              setSuccess(successMessage || (shouldPrint ? 'Print dialog opened' : 'PDF downloaded successfully'));
              // Don't regenerate PDF to avoid view refresh
            }
          } catch (err: any) {
            setError(err.message || (saveOnly ? 'Failed to save form data' : (shouldPrint ? 'Failed to print PDF' : 'Failed to download PDF')));
          } finally {
            // Re-enable PDF regeneration after a short delay to allow operations to complete
            setTimeout(() => {
              setSkipPdfRegeneration(false);
            }, 1000);
          }
        }}
        onRetry={handleRetryFromParent}
      />
    );
  };

// PDF Viewer using iframe - simple and reliable
const PDFViewerIframe: React.FC<{ document: JobDocument }> = ({ document }) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create a blob URL from the downloaded PDF
        const blob = await documentService.downloadDocument(document.id);
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);

        setLoading(false);
      } catch (err: any) {
        console.error('Failed to load PDF:', err);
        setError(`Failed to load PDF: ${err.message}`);
        setLoading(false);
      }
    };

    loadPdf();

    // Cleanup blob URL when component unmounts
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [document.id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'grey.50',
          p: 4
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading PDF...</Typography>
        <Typography variant="body2" color="text.secondary">
          {document.originalFileName}
        </Typography>
      </Box>
    );
  }

  if (error || !pdfUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          backgroundColor: 'grey.100',
          p: 4
        }}
      >
        <PdfIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {error || 'Failed to load PDF'}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {document.originalFileName}
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await documentService.saveDocument(document);
            } catch (err) {
              console.error('Download failed:', err);
              alert('Download failed. Please try again.');
            }
          }}
          sx={{ mt: 2 }}
        >
          Download PDF
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Use full height of parent (lightbox slide)
        width: '90%', // Use 90% width as requested
        backgroundColor: 'grey.50',
        margin: 'auto'
      }}
    >
      {/* PDF Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PdfIcon color="primary" />
          <Typography variant="h6" noWrap sx={{ maxWidth: '400px' }}>
            {document.originalFileName}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={async (e) => {
            e.stopPropagation();
            try {
              await documentService.saveDocument(document);
            } catch (err) {
              console.error('Download failed:', err);
              alert('Download failed. Please try again.');
            }
          }}
          size="small"
        >
          Download
        </Button>
      </Box>

      {/* PDF Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          backgroundColor: 'white'
        }}
      >
        <iframe
          src={pdfUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title={`PDF: ${document.originalFileName}`}
        />
      </Box>
    </Box>
  );
};

const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const [job, setJob] = useState<Job | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [formResponses, setFormResponses] = useState<{ [formId: number]: any }>({});
  const [allJobFormResponses, setAllJobFormResponses] = useState<any[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingFormResponses, setLoadingFormResponses] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);
  const [duplicateAddress, setDuplicateAddress] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());

  // Document state
  const [uploadedDocuments, setUploadedDocuments] = useState<JobDocument[]>([]);
  const [uploadConstraints, setUploadConstraints] = useState<UploadConstraints | null>(null);

  // Batch upload state
  const [isBatchUploading, setIsBatchUploading] = useState(false);
  const [batchSelectedFiles, setBatchSelectedFiles] = useState<any[]>([]);

  // Gallery/Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState<any[]>([]);
  const [lightboxSection, setLightboxSection] = useState<'uploaded' | 'other'>('uploaded');
  const [lightboxLoading, setLightboxLoading] = useState(false);

  // Shared thumbnail and full image URLs (shared between gallery and lightbox to prevent duplicate requests)
  const [sharedThumbnailUrls, setSharedThumbnailUrls] = useState<{ [key: string]: string | null }>({});
  const [sharedFullImageUrls, setSharedFullImageUrls] = useState<{ [key: string]: string | null }>({});

  // PDF state for PixelVersion forms
  const [pixelVersionPdfBytes, setPixelVersionPdfBytes] = useState<{ [formId: number]: Uint8Array }>({});
  const [loadingPixelVersionPdfs, setLoadingPixelVersionPdfs] = useState<{ [formId: number]: boolean }>({});

  // Prevent PDF regeneration after user operations (download/save/print)
  const skipPdfRegenerationRef = useRef(false);

  // Function to set the regeneration skip flag
  const setSkipPdfRegeneration = useCallback((skip: boolean) => {
    skipPdfRegenerationRef.current = skip;
  }, []);

  // Prevent duplicate initial loads in StrictMode/dev
  const hasLoadedFormsRef = useRef(false);

  // Track which images are currently being loaded to prevent duplicate requests
  const loadingImagesRef = useRef<Set<string>>(new Set());

  // Update lightbox slides when full images load
  useEffect(() => {
    if (lightboxOpen && lightboxSlides.length > 0) {
      // Get section-filtered documents
      const sectionDocuments = uploadedDocuments.filter(d => {
        const ext = d.fileExtension.toLowerCase().replace('.', '');
        const isViewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);

        if (!isViewable) return false;

        const isUploaded = d.documentType === 1 || d.documentType === 'Uploaded';
        if (lightboxSection === 'uploaded') {
          return isUploaded;
        } else {
          return !isUploaded;
        }
      });

      // Update slides with latest image URLs
      setLightboxSlides(prepareGallerySlides(sectionDocuments));
    }
  }, [sharedFullImageUrls, sharedThumbnailUrls, lightboxOpen]);


  const [formData, setFormData] = useState<UpdateJobDto>({
    id: '',
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium
  });

  // Load forms and appointment types when component mounts, but delay business loading
  // This runs as soon as user?.businessId is available
  useEffect(() => {
    if (user?.businessId && !hasLoadedFormsRef.current) {
      hasLoadedFormsRef.current = true;
      // Load forms and appointment types immediately, defer business until needed
      loadForms();
      loadAppointmentTypes();
    }
  }, [user?.businessId]);

  // Load job when id is available (runs in parallel with forms/business loading)
  useEffect(() => {
    if (id) {
      loadJob();
    }
  }, [id]);

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setIsOffline(!customEvent.detail.isOnline);
    };

    // Listen for offline/online events
    window.addEventListener('connectivity-changed', handleConnectivityChange);

    // Also check offline service status periodically to catch any missed updates
    const checkOfflineStatus = () => {
      const isOnline = offlineService.getOnlineStatus();
      setIsOffline(!isOnline);
    };

    // Check immediately and then every 2 seconds
    checkOfflineStatus();
    const interval = setInterval(checkOfflineStatus, 2000);

    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
      clearInterval(interval);
    };
  }, []);

  // Generate PDFs for PixelVersion forms when forms, responses, job or customer change
  useEffect(() => {
    if (!job || forms.length === 0 || skipPdfRegenerationRef.current) return;

    // Generate PDFs for all PixelVersion forms
    forms.forEach(form => {
      if (isPixelVersionForm(form) && !loadingPixelVersionPdfs[form.id]) {
        generatePixelVersionPdf(form);
      }
    });
  }, [forms, formResponses, job, customer]);

  // Load upload constraints on mount
  useEffect(() => {
    const loadConstraints = async () => {
      try {
        const constraints = await documentService.getUploadConstraints();
        setUploadConstraints(constraints);
      } catch (err) {
        console.error('Failed to load upload constraints:', err);
      }
    };
    loadConstraints();
  }, []);

  // Load form responses when forms are loaded and a form tab is active
  useEffect(() => {
    if (forms.length > 0 && id && tabValue >= 4) {
      const formIndex = tabValue - 4;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        if (!formResponses[form.id] && !loadingFormResponses) {
          loadFormResponses(form.id);
        }
      }
    }
  }, [forms, tabValue, id]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadJob = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Try API first, fallback to IndexedDB if it fails
      try {
        console.log('[JobDetailPage] Attempting to load job from API');
        const jobData = await jobService.getJob(id);
        setJob(jobData);

        // Load customer details
        if (jobData.customerId) {
          loadCustomer(jobData.customerId);
        }
      } catch (apiError: any) {
        console.log('[JobDetailPage] API failed, trying IndexedDB fallback:', apiError.message);

        // API failed, try IndexedDB
        const jobData = await indexedDbService.getJob(id);
        if (jobData) {
          console.log('[JobDetailPage] Successfully loaded job from IndexedDB');
          setJob(jobData);

          // Load customer details from IndexedDB
          if (jobData.customerId) {
            loadCustomerOffline(jobData.customerId);
          }
        } else {
          setError('Job not found in offline data. Please check your internet connection and try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomer = async (customerId: string) => {
    try {
      const customerData = await customerService.getCustomer(customerId);
      setCustomer(customerData);
    } catch (err: any) {
      console.error('Failed to load customer from API, trying IndexedDB:', err);
      // Fallback to IndexedDB
      try {
        const customerData = await indexedDbService.getCustomer(customerId);
        if (customerData) {
          setCustomer(customerData);
        }
      } catch (indexedDbErr: any) {
        console.error('Failed to load customer from IndexedDB:', indexedDbErr);
      }
    }
  };

  const loadCustomerOffline = async (customerId: string) => {
    try {
      const customerData = await indexedDbService.getCustomer(customerId);
      if (customerData) {
        setCustomer(customerData);
      }
    } catch (err: any) {
      console.error('Failed to load customer from IndexedDB:', err);
    }
  };

  const loadBusiness = async () => {
    try {
      const businessData = await businessService.getBusinesses();
      if (businessData && businessData.length > 0) {
        setBusiness(businessData[0]);
      }
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const loadAppointmentTypes = async () => {
    try {
      setLoadingAppointmentTypes(true);
      const appointmentTypesData = await appointmentTypeService.getAppointmentTypes(user?.businessId || undefined);
      setAppointmentTypes(appointmentTypesData);
    } catch (err: any) {
      console.error('Failed to load appointment types:', err);
    } finally {
      setLoadingAppointmentTypes(false);
    }
  };

  const loadForms = async () => {
    try {
      setLoadingForms(true);
      const formsData = await formService.getForms();
      setForms(formsData);
    } catch (err: any) {
      console.error('Failed to load forms:', err);
      setError(err.message || 'Failed to load forms');
    } finally {
      setLoadingForms(false);
    }
  };

  const loadAppointments = async () => {
    if (!id) return;

    try {
      setLoadingAppointments(true);

      // Check if we're offline
      const isCurrentlyOffline = !offlineService.getOnlineStatus();

      if (isCurrentlyOffline) {
        console.log('[JobDetailPage] Loading appointments from IndexedDB (offline mode)');

        // Load appointments from IndexedDB and filter by jobId
        const allAppointments = await indexedDbService.getAppointmentsForBusiness(user?.businessId || '');
        const jobAppointments = allAppointments.filter(appointment => appointment.jobId === id);

        console.log(`[JobDetailPage] Found ${jobAppointments.length} appointments for job ${id} in IndexedDB`);
        setAppointments(jobAppointments);
      } else {
        // Online mode - use API
        const appointmentsData = await appointmentService.getAppointmentsByJobId(id);
        setAppointments(appointmentsData);
      }
    } catch (err: any) {
      console.error('Failed to load appointments:', err);
      setError(err.message || 'Failed to load appointments');
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handleEditClick = async () => {
    if (!job) return;

    // Load business data if not already loaded
    if (!business) {
      await loadBusiness();
    }

    // Convert string enum values to numeric enum values for form
    const convertJobType = (type: JobType | string): JobType => {
      if (typeof type === 'string') {
        const stringToEnum: { [key: string]: JobType } = {
          'KitchenDesign': JobType.KitchenDesign,
          'KitchenRemodel': JobType.KitchenRemodel,
          'NewKitchen': JobType.NewKitchen,
          'Consultation': JobType.Consultation,
          'Renovation': JobType.Renovation,
          'Other': JobType.Other
        };
        return stringToEnum[type] || JobType.Consultation;
      }
      return type as JobType;
    };

    const convertPriority = (priority: Priority | string): Priority => {
      if (typeof priority === 'string') {
        const stringToEnum: { [key: string]: Priority } = {
          'Low': Priority.Low,
          'Medium': Priority.Medium,
          'High': Priority.High,
          'Urgent': Priority.Urgent
        };
        return stringToEnum[priority] || Priority.Medium;
      }
      return priority as Priority;
    };

    setFormData({
      id: job.id,
      customerId: job.customerId,
      assignedSalesPersonId: job.assignedSalesPersonId,
      jobAddress: job.jobAddress || '',
      type: convertJobType(job.type),
      estimatedValue: job.estimatedValue,
      actualValue: job.actualValue,
      priority: convertPriority(job.priority),
      expectedCompletionDate: job.expectedCompletionDate,
      notes: job.notes || ''
    });

    setIsEditDialogOpen(true);
  };

  const handleDuplicateClick = () => {
    if (!job) return;
    setDuplicateAddress(job.jobAddress || '');
    setIsDuplicateDialogOpen(true);
  };

  const handleDuplicateJob = async () => {
    if (!job) return;

    try {
      setIsDuplicating(true);
      setError(null);

      const duplicateDto: DuplicateJobDto = {
        sourceJobId: job.id,
        jobAddress: duplicateAddress || undefined
      };

      const newJob = await jobService.duplicateJob(duplicateDto);

      setIsDuplicateDialogOpen(false);
      setSuccess('Job duplicated successfully. Opening new job...');

      // Open the new job in a new tab
      const newJobUrl = `${window.location.origin}/crm/jobs/${newJob.id}`;
      window.open(newJobUrl, '_blank');
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const validationErrors = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${validationErrors}`);
      } else {
        setError(err.message || 'Failed to duplicate job');
      }
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleUpdateJob = async () => {
    if (!job) return;

    try {
      // Clean up form data - convert empty strings to undefined for optional fields
      const cleanedFormData = {
        ...formData,
        jobAddress: formData.jobAddress || undefined,
        expectedCompletionDate: formData.expectedCompletionDate || undefined,
        notes: formData.notes || undefined
      };

      await jobService.updateJob(cleanedFormData);
      setIsEditDialogOpen(false);
      setSuccess('Job updated successfully');
      setError(null);
      loadJob();
    } catch (err: any) {
      if (err.response?.data?.errors) {
        const validationErrors = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        setError(`Validation errors:\n${validationErrors}`);
      } else {
        setError(err.message || 'Failed to update job');
      }
      setSuccess(null);
    }
  };

  const handleDeleteJob = async () => {
    if (!job || !window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await jobService.deleteJob(job.id);
      navigate('/crm/jobs');
    } catch (err: any) {
      setError(err.message || 'Failed to delete job');
    }
  };

  const handleBack = () => {
    navigate('/crm/jobs');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);

    // Load appointments when switching to appointments tab
    if (newValue === 1 && appointments.length === 0 && !loadingAppointments) {
      loadAppointments();
    }

    // Always reload all form responses when switching to documents tab to get latest data
    if (newValue === 3 && !loadingDocuments && id) {
      loadAllFormResponses();
    }

    // Load form responses when switching to a form tab
    if (newValue >= 4 && id) {
      const formIndex = newValue - 4;
      if (formIndex >= 0 && formIndex < forms.length) {
        const form = forms[formIndex];
        if (!formResponses[form.id] && !loadingFormResponses) {
          loadFormResponses(form.id);
        }
      }
    }
  };

  const loadAllFormResponses = async () => {
    if (!id) return;

    try {
      setLoadingDocuments(true);
      // Load both form responses and uploaded documents in parallel
      const [responses, documents] = await Promise.all([
        formService.getJobFormResponses(id),
        documentService.getDocumentsForJob(id)
      ]);
      setAllJobFormResponses(responses);
      setUploadedDocuments(documents);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const loadUploadedDocuments = async (forceRefresh = false) => {
    if (!id) return;

    try {
      const documents = await documentService.getDocumentsForJob(id, forceRefresh);
      setUploadedDocuments(documents);
    } catch (err: any) {
      console.error('Failed to load uploaded documents:', err);
    }
  };

  const loadFormResponses = async (formId: number, updateState: boolean = true): Promise<{ [key: string]: any } | undefined> => {
    if (!id) return undefined;

    try {
      setLoadingFormResponses(true);

      // Get all job form responses
      const responses = await formService.getJobFormResponses(id);

      // Filter responses for this form and transform to form data
      const form = forms.find(f => f.id === formId);
      if (form) {
        // Filter responses by form - need to check if response belongs to this form
        // Responses have FormFieldId, so we need to check if that field belongs to this form
        const formFieldIds = new Set<number>();
        form.formSections?.forEach(section => {
          section.formFields?.forEach(field => {
            formFieldIds.add(field.id);
          });
        });

        const formResponsesForForm = responses.filter(response =>
          formFieldIds.has(response.formFieldId)
        );

        // Transform to form data format
        const formData = formService.transformJobFormResponsesToFormData(
          formResponsesForForm,
          form
        );

        if (updateState) {
          setFormResponses(prev => ({
            ...prev,
            [formId]: formData
          }));
        }

        return formData;
      }
    } catch (err: any) {
      console.error('Failed to load form responses:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingFormResponses(false);
    }

    return undefined;
  };


  // Generate document filename using the same format as FormRenderer
  const generateDocumentFilename = (form: Form): string => {
    const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const sanitizedJobNumber = job?.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
    return `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;
  };

  // Get forms that have been filled out (have responses) and are printable
  const getFormsWithResponses = (): Form[] => {
    if (allJobFormResponses.length === 0) return [];

    // Create a map of form IDs to their field IDs
    const formFieldIdMap = new Map<number, Set<number>>();
    forms.forEach(form => {
      const fieldIds = new Set<number>();
      form.formSections?.forEach(section => {
        section.formFields?.forEach(field => {
          fieldIds.add(field.id);
        });
      });
      formFieldIdMap.set(form.id, fieldIds);
    });

    // Find forms that have at least one response and are printable
    const formsWithResponses: Form[] = [];
    forms.forEach(form => {
      // Only include forms that are printable
      if (!form.isPrintable) return;

      const fieldIds = formFieldIdMap.get(form.id);
      if (fieldIds) {
        const hasResponse = allJobFormResponses.some(response =>
          fieldIds.has(response.formFieldId)
        );
        if (hasResponse) {
          formsWithResponses.push(form);
        }
      }
    });

    // Sort by DisplayOrder
    return formsWithResponses.sort((a, b) => a.displayOrder - b.displayOrder);
  };

  // Helper function to check if form has PixelVersion enabled
  const isPixelVersionForm = (form: Form): boolean => {
    if (!form.pdfConfigurationJson) return false;

    try {
      const pdfConfig = JSON.parse(form.pdfConfigurationJson);
      return pdfConfig.pixelVersion === true;
    } catch (error) {
      console.warn('Failed to parse PDF configuration JSON:', error);
      return false;
    }
  };

  // Generate PDF for PixelVersion forms
  const generatePixelVersionPdf = async (form: Form) => {
    if (!id || !job) return;

    try {
      setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: true }));
      setError(null);

      // Fetch parameter values and form responses in parallel
      // Don't update state to avoid triggering PDF regeneration useEffect loop
      const [formData, formParameterValues] = await Promise.all([
        loadFormResponses(form.id, false),
        formService.getFormFieldParameterValues(id, form.id)
      ]);
      const initialFormData = formData || {};

      // Get form sections
      const formSections = form.formSections || [];

      // Generate filename
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

      // Parse PDF configuration from form
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }

      // Prepare header data for placeholder replacement (e.g., {{ DisplayName }})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: job.assignedSalesPersonName || ''
      };

      // Generate PDF with PixelVersion flag
      const pdfBytes = await PDFService.generateEditablePDF(
        initialFormData,
        form.description,
        formSections,
        {
          filename,
          headerConfigurationJson: form.headerConfigurationJson,
          headerHeight: pdfConfig.headerHeight ?? 100,
          pageMargin: pdfConfig.pageMargin ?? 15,
          headerData,
          defaultFontSize: pdfConfig.defaultFontSize,
          labelFontSize: pdfConfig.labelFontSize,
          multilineFontSize: pdfConfig.multilineFontSize,
          sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
          fieldSpacing: pdfConfig.fieldSpacing,
          sectionSpacing: pdfConfig.sectionSpacing,
          checkboxListSpacing: pdfConfig.checkboxListSpacing,
          sectionPadding: pdfConfig.sectionPadding,
          fieldHeight: pdfConfig.fieldHeight,
          showSectionBorders: pdfConfig.showSectionBorders,
          checkboxListColumns: pdfConfig.checkboxListColumns,
          pixelVersion: true, // Enable PixelVersion mode
          job,
          customer,
          formParameterValues,
          userDataFields: user?.userDataFields
        }
      );

      // Store PDF bytes for display
      setPixelVersionPdfBytes(prev => ({ ...prev, [form.id]: pdfBytes }));
      setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: false }));
    } catch (err: any) {
      console.error('Error generating PixelVersion PDF:', err);
      setError(err.message || 'Failed to generate PDF');
      setLoadingPixelVersionPdfs(prev => ({ ...prev, [form.id]: false }));
    }
  };

  // Document upload handlers


  const handleDocumentClick = async (document: JobDocument) => {
    try {
      await documentService.openDocument(document);
    } catch (err: any) {
      console.error('Failed to open document:', err);
      setError('Failed to open document');
    }
  };

  const handleDownloadDocument = async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await documentService.saveDocument(document);
    } catch (err: any) {
      console.error('Failed to download document:', err);
      setError('Failed to download document');
    }
  };

  const handleDeleteDocument = async (document: JobDocument, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${document.originalFileName}"?`)) return;

    try {
      await documentService.deleteDocument(document.id);
      setSuccess('Document deleted successfully');
      await loadUploadedDocuments();
    } catch (err: any) {
      console.error('Failed to delete document:', err);
      setError('Failed to delete document');
    }
  };



  // Callback when thumbnail is loaded in gallery
  const handleThumbnailLoaded = (documentId: string, url: string | null) => {
    setSharedThumbnailUrls(prev => ({ ...prev, [documentId]: url }));
  };

  // Prepare gallery slides for lightbox
  const prepareGallerySlides = (documents: JobDocument[]) => {
    return documents
      .filter(doc => {
        // Only include images and PDFs for gallery
        const ext = doc.fileExtension.toLowerCase().replace('.', '');
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);
      })
      .map(doc => {
        const ext = doc.fileExtension.toLowerCase().replace('.', '');
        if (ext === 'pdf') {
          // For PDFs, create a slide with PDF icon
          return {
            src: '', // Empty src for PDFs
            alt: doc.originalFileName,
            type: 'custom',
            document: doc
          };
        } else {
          // For images, prefer full image, then thumbnail (from shared state), then empty
          const fullImageUrl = sharedFullImageUrls[doc.id];
          const thumbnailUrl = sharedThumbnailUrls[doc.id];

          return {
            src: fullImageUrl || thumbnailUrl || '',
            alt: doc.originalFileName,
            type: 'image',
            document: doc
          };
        }
      });
  };

  // Helper function to load full image data URL with deduplication
  const loadFullImageDataUrl = async (documentId: string): Promise<string | null> => {
    // Check if already loaded
    if (sharedFullImageUrls[documentId]) {
      return sharedFullImageUrls[documentId];
    }

    // Check if currently loading
    if (loadingImagesRef.current.has(documentId)) {
      console.log(`[Lightbox] Skipping duplicate load request for ${documentId}`);
      return null;
    }

    // Mark as loading
    loadingImagesRef.current.add(documentId);
    console.log(`[Lightbox] Loading full image for ${documentId}`);

    try {
      const blob = await documentService.downloadDocument(documentId);
      const dataUrl = await new Promise<string | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });

      // Store the result
      setSharedFullImageUrls(prev => ({ ...prev, [documentId]: dataUrl }));

      return dataUrl;
    } catch (error) {
      console.warn('Failed to load full image for lightbox:', documentId, error);
      return null;
    } finally {
      // Remove from loading set
      loadingImagesRef.current.delete(documentId);
    }
  };

  const handleDocumentGalleryClick = async (document: JobDocument, section: 'uploaded' | 'other') => {
    // Filter documents by section
    const sectionDocuments = uploadedDocuments.filter(d => {
      const ext = d.fileExtension.toLowerCase().replace('.', '');
      const isViewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);

      if (!isViewable) return false;

      // Filter by section
      const isUploaded = d.documentType === 1 || d.documentType === 'Uploaded';
      if (section === 'uploaded') {
        return isUploaded;
      } else {
        return !isUploaded;
      }
    });

    const clickedIndex = sectionDocuments.findIndex(d => d.id === document.id);

    if (clickedIndex >= 0) {
      setLightboxLoading(true);
      setLightboxSection(section);

      try {
        const clickedDocument = sectionDocuments[clickedIndex];
        const ext = clickedDocument.fileExtension.toLowerCase().replace('.', '');
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);

        // Load thumbnail/full image if not already loaded AND it's an image (not PDF)
        if (isImage && !sharedThumbnailUrls[clickedDocument.id]) {
          if (clickedDocument.hasThumbnail) {
            // Load thumbnail for images that have one
            try {
              const thumbnailUrl = await documentService.getThumbnailDataUrl(clickedDocument.id);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: thumbnailUrl }));
            } catch (error) {
              console.warn('Failed to load thumbnail for clicked image:', clickedDocument.id, error);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: null }));
            }
          } else {
            // Load full image for images without thumbnails (small images)
            try {
              const blob = await documentService.downloadDocument(clickedDocument.id);
              const dataUrl = await new Promise<string | null>((resolve) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: dataUrl }));
            } catch (error) {
              console.warn('Failed to load full image for clicked image:', clickedDocument.id, error);
              setSharedThumbnailUrls(prev => ({ ...prev, [clickedDocument.id]: null }));
            }
          }
        }

        // Open lightbox immediately with slides (will use already-loaded thumbnails/images from gallery)
        const slides = prepareGallerySlides(sectionDocuments);
        setLightboxSlides(slides);
        setLightboxIndex(clickedIndex);
        setLightboxOpen(true);

        // Load full resolution image for images with thumbnails (effect will update slides when loaded)
        // Skip for images without thumbnails since we already loaded the full image above
        if (isImage && clickedDocument.hasThumbnail && !sharedFullImageUrls[clickedDocument.id] && !loadingImagesRef.current.has(clickedDocument.id)) {
          loadFullImageDataUrl(clickedDocument.id);
        }

      } catch (error) {
        console.error('Failed to load lightbox for clicked image:', error);
        // Still open lightbox - they'll show fallback UI
        const slides = prepareGallerySlides(sectionDocuments);
        setLightboxSlides(slides);
        setLightboxIndex(clickedIndex);
        setLightboxOpen(true);
      } finally {
        setLightboxLoading(false);
      }
    } else {
      // Fallback to regular document opening
      handleDocumentClick(document);
    }
  };


  const handlePrintDocument = async (form: Form) => {
    if (!id || !job) return;

    try {
      setError(null);

      // Always reload form responses to ensure we have the latest data
      const [formData, formParameterValues] = await Promise.all([
        loadFormResponses(form.id),
        formService.getFormFieldParameterValues(id, form.id)
      ]);
      const initialFormData = formData || {};

      // Debug: Log to verify data is loaded
      console.log('Printing document for form:', form.description);
      console.log('Form data loaded:', initialFormData);
      console.log('Form data keys:', Object.keys(initialFormData));
      console.log('Form data has values:', Object.keys(initialFormData).length > 0);

      // Get form sections
      const formSections = form.formSections || [];

      // Generate filename
      const sanitizedFormName = form.description.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const sanitizedJobNumber = job.jobNumber ? job.jobNumber.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') : 'job';
      const filename = `${sanitizedJobNumber}-${sanitizedFormName}.pdf`;

      // Parse PDF configuration from form if available
      let pdfConfig: any = {};
      if (form.pdfConfigurationJson) {
        try {
          pdfConfig = JSON.parse(form.pdfConfigurationJson);
        } catch (error) {
          console.warn('Failed to parse PDF configuration JSON:', error);
        }
      }

      // Prepare header data for placeholder replacement (e.g., {{ DisplayName }})
      const headerData: { [key: string]: string | number | boolean | null | undefined } = {
        DisplayName: job.assignedSalesPersonName || ''
      };

      // Generate PDF
      const pdfBytes = await PDFService.generateEditablePDF(
        initialFormData,
        form.description,
        formSections,
        {
          filename,
          headerConfigurationJson: form.headerConfigurationJson,
          headerHeight: pdfConfig.headerHeight ?? 100,
          pageMargin: pdfConfig.pageMargin ?? 15,
          headerData,
          defaultFontSize: pdfConfig.defaultFontSize,
          labelFontSize: pdfConfig.labelFontSize,
          multilineFontSize: pdfConfig.multilineFontSize,
          sectionTitleFontSize: pdfConfig.sectionTitleFontSize,
          fieldSpacing: pdfConfig.fieldSpacing,
          sectionSpacing: pdfConfig.sectionSpacing,
          checkboxListSpacing: pdfConfig.checkboxListSpacing,
          sectionPadding: pdfConfig.sectionPadding,
          fieldHeight: pdfConfig.fieldHeight,
          showSectionBorders: pdfConfig.showSectionBorders,
          job,
          customer,
          formParameterValues
        }
      );

      // Create blob and open in new tab
      const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Open PDF in new tab - user can print from there
      window.open(url, '_blank');

      // Clean up URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);
    } catch (err: any) {
      console.error('Error printing document:', err);
      setError(err.message || 'Failed to generate PDF for printing');
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    const appointmentDate = new Date(appointment.startTime);
    navigate('/calendar', {
      state: {
        date: appointmentDate.toISOString(),
        appointmentId: appointment.id
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header skeleton */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box>
              <Skeleton variant="text" width={200} height={40} />
              <Skeleton variant="text" width={150} height={24} />
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Skeleton variant="rectangular" width={100} height={36} />
            <Skeleton variant="rectangular" width={100} height={36} />
          </Box>
        </Box>

        {/* Tabs skeleton */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
            <Skeleton variant="rectangular" width={100} height={48} />
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={140} height={48} />
            <Skeleton variant="rectangular" width={120} height={48} />
            <Skeleton variant="rectangular" width={100} height={48} />
          </Box>
        </Paper>

        {/* Content skeleton */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 8 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ backgroundColor: theme.accentColor }}>
              <CardContent>
                <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
                <Divider sx={{ mb: 2 }} />
                {Array.from({ length: 4 }).map((_, index) => (
                  <Box key={index} display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1.5 }} />
                    <Box flex={1}>
                      <Skeleton variant="text" width={100} height={16} />
                      <Skeleton variant="text" width={200} height={24} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  if (!loading && !job) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Job not found
        </Alert>
        <Button variant="contained" onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Back to Jobs
        </Button>
      </Box>
    );
  }

  // Type guard: if we've passed all checks, job should exist
  if (!job) {
    return null;
  }

  // Build the title string
  const getPageTitle = () => {
    const parts: string[] = [];
    if (job.jobAddress) {
      // Get just the first part of the address (before first comma or newline)
      const firstPart = job.jobAddress.split(',')[0].split('\n')[0].trim();
      parts.push(firstPart);
    }
    if (customer) {
      parts.push(`${customer.firstName} ${customer.lastName}`);
    }
    if (parts.length === 0) {
      return job.jobNumber;
    }
    return parts.join(' - ');
  };

  const pageTitle = getPageTitle();

  return (
    <>
      <Helmet>
        <title>Job: {pageTitle} | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleBack}
              color="primary"
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" component="h1">
                {pageTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {job.jobNumber}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              disabled={isOffline}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              startIcon={<DuplicateIcon />}
              onClick={handleDuplicateClick}
              disabled={isOffline}
            >
              Duplicate
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteJob}
              disabled={isOffline}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ backgroundColor: theme.accentColor, mb: 3, borderRadius: 0 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="job detail tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab label="Overview" />
            <Tab label="Appointments" />
            <Tab label="Communication" />
            <Tab label="Documents" />
            {loadingForms ? (
              // Show skeleton tabs while forms are loading
              <>
                {[1, 2, 3].map((index) => (
                  <Tab
                    key={`skeleton-${index}`}
                    disabled
                    label={
                      <Skeleton
                        variant="text"
                        width={80}
                        height={20}
                        sx={{ display: 'inline-block' }}
                      />
                    }
                  />
                ))}
              </>
            ) : (
              forms.map((form, index) => (
                <Tab key={form.id} label={form.description} />
              ))
            )}
          </Tabs>
        </Paper>

        {/* Tab Panels */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Job Details Card */}
            <Grid item xs={12} md={6}>
              <JobDetailsCard job={job} theme={theme} />
            </Grid>

            {/* Customer Details Card */}
            <Grid item xs={12} md={6}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Customer Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {customer ? (
                    <CustomerDetailsCard customer={customer} />
                  ) : (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AppointmentsList
            appointments={appointments}
            appointmentTypes={appointmentTypes}
            loadingAppointments={loadingAppointments}
            theme={theme}
            onAppointmentClick={handleAppointmentClick}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card sx={{ backgroundColor: theme.accentColor }}>
            <CardContent>
              <Typography variant="h6">Communication</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Communication logs for this job will be displayed here.
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <DocumentGallery
            jobId={id || ''}
            uploadedDocuments={uploadedDocuments}
            loadingDocuments={loadingDocuments}
            isOffline={isOffline}
            uploadConstraints={uploadConstraints}
            onDocumentClick={handleDocumentClick}
            onDocumentGalleryClick={handleDocumentGalleryClick}
            onDownloadDocument={handleDownloadDocument}
            onDeleteDocument={handleDeleteDocument}
            onReloadDocuments={loadUploadedDocuments}
            thumbnailUrls={sharedThumbnailUrls}
            onThumbnailLoaded={handleThumbnailLoaded}
          />
        </TabPanel>

        {/* Dynamic Form Tabs */}
        {forms.map((form, index) => {
          const tabIndex = 4 + index; // Start after the static tabs (Overview, Appointments, Communication, Documents)
          const isPixelVersion = isPixelVersionForm(form);

          return (
            <TabPanel key={form.id} value={tabValue} index={tabIndex}>
              <Card sx={{ backgroundColor: theme.accentColor }}>
                <CardContent>
                  {loadingForms ? (
                    <Box display="flex" justifyContent="center" py={3}>
                      <CircularProgress />
                    </Box>
                  ) : isPixelVersion ? (
                    // Display PDF viewer for PixelVersion forms
                    <PixelVersionPDFViewer
                      pdfBytes={pixelVersionPdfBytes[form.id] || null}
                      loading={loadingPixelVersionPdfs[form.id] || false}
                      form={form}
                      job={job}
                      customer={customer}
                      loadFormResponses={loadFormResponses}
                      generatePixelVersionPdf={generatePixelVersionPdf}
                      setSuccess={setSuccess}
                      setError={setError}
                      setSkipPdfRegeneration={setSkipPdfRegeneration}
                      userDataFields={user?.userDataFields}
                    />
                  ) : (
                    // Display FormRenderer for regular forms
                    <FormRenderer
                      form={form}
                      jobId={id}
                      jobNumber={job.jobNumber}
                      customerId={customer?.id}
                      assignedSalesPersonName={job.assignedSalesPersonName}
                      job={job}
                      customer={customer}
                      existingResponse={formResponses[form.id]}
                      isLoadingResponses={loadingFormResponses}
                      onSubmit={(responseData) => {
                        setSuccess('Form submitted successfully');
                        // Reload form responses after submission
                        if (id) {
                          loadFormResponses(form.id);
                        }
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          );
        })}

        {/* Edit Job Dialog */}
        <JobEditDialog
          open={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onUpdate={handleUpdateJob}
          job={job}
          customer={customer}
          business={business}
          formData={formData}
          onFormDataChange={(data) => setFormData({ ...formData, ...data })}
          error={error}
          success={success}
        />

        {/* Duplicate Job Dialog */}
        <DuplicateJobDialog
          open={isDuplicateDialogOpen}
          onClose={() => setIsDuplicateDialogOpen(false)}
          onDuplicate={handleDuplicateJob}
          duplicateAddress={duplicateAddress}
          onAddressChange={setDuplicateAddress}
          isDuplicating={isDuplicating}
        />

        {/* Document Gallery Lightbox */}
        <Lightbox
          open={lightboxOpen}
          close={() => {
            setLightboxOpen(false);
            setLightboxLoading(false);
            // Don't clear shared URLs - they're shared with the gallery
          }}
          index={lightboxIndex}
          carousel={{ finite: false }}
          animation={{ fade: 300 }}
          slides={lightboxSlides.map(slide => {
            if (slide.type === 'custom') {
              // For PDFs, we'll handle custom rendering in the render prop
              return {
                src: slide.src || '',
                alt: slide.alt,
                type: 'custom' as any,
                document: slide.document
              };
            } else {
              // For images - use the API URL we set in prepareGallerySlides
              return {
                src: slide.src,
                alt: slide.alt,
                type: 'image',
                document: slide.document
              };
            }
          })}
          render={{
            slide: ({ slide }: any) => {
              if (slide.type === 'custom' && slide.document) {
                // PDF viewer using iframe - no external dependencies
                return (
                  <PDFViewerIframe document={slide.document} />
                );
              }

              // For images, handle cases where thumbnail failed to load
              if (slide.type === 'image' && (!slide.src || slide.src === '')) {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      backgroundColor: 'grey.100',
                      p: 4
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 80, color: 'grey.500', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Failed to load image
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {slide.alt}
                    </Typography>
                  </Box>
                );
              }

              return undefined; // Let lightbox handle images normally
            }
          }}
          on={{
            view: ({ index }: any) => {
              setLightboxIndex(index);

              // Get section-filtered documents (same filtering as in handleDocumentGalleryClick)
              const sectionDocuments = uploadedDocuments.filter(d => {
                const ext = d.fileExtension.toLowerCase().replace('.', '');
                const isViewable = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf'].includes(ext);

                if (!isViewable) return false;

                const isUploaded = d.documentType === 1 || d.documentType === 'Uploaded';
                if (lightboxSection === 'uploaded') {
                  return isUploaded;
                } else {
                  return !isUploaded;
                }
              });

              if (index >= 0 && index < sectionDocuments.length) {
                const currentDocument = sectionDocuments[index];
                const ext = currentDocument.fileExtension.toLowerCase().replace('.', '');
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);

                // Only load images (not PDFs)
                if (isImage) {
                  // Load thumbnail/full image if not already loaded
                  if (!sharedThumbnailUrls[currentDocument.id]) {
                    if (currentDocument.hasThumbnail) {
                      // Load thumbnail for images that have one
                      documentService.getThumbnailDataUrl(currentDocument.id)
                        .then((thumbnailUrl) => {
                          setSharedThumbnailUrls(prev => ({
                            ...prev,
                            [currentDocument.id]: thumbnailUrl
                          }));
                          // Effect will update slides automatically
                        })
                        .catch(error => {
                          console.warn('Failed to load thumbnail for navigated image:', currentDocument.id, error);
                          setSharedThumbnailUrls(prev => ({
                            ...prev,
                            [currentDocument.id]: null
                          }));
                        });
                    } else {
                      // Load full image for images without thumbnails
                      documentService.downloadDocument(currentDocument.id)
                        .then(blob => {
                          return new Promise<string | null>((resolve) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result as string);
                            reader.onerror = () => resolve(null);
                            reader.readAsDataURL(blob);
                          });
                        })
                        .then(dataUrl => {
                          setSharedThumbnailUrls(prev => ({
                            ...prev,
                            [currentDocument.id]: dataUrl
                          }));
                          // Effect will update slides automatically
                        })
                        .catch(error => {
                          console.warn('Failed to load full image for navigated image:', currentDocument.id, error);
                          setSharedThumbnailUrls(prev => ({
                            ...prev,
                            [currentDocument.id]: null
                          }));
                        });
                    }
                  }

                  // Load high-resolution image for images with thumbnails (if not already loaded)
                  // Skip for images without thumbnails since we already have the full image
                  if (currentDocument.hasThumbnail && !sharedFullImageUrls[currentDocument.id] && !loadingImagesRef.current.has(currentDocument.id)) {
                    loadFullImageDataUrl(currentDocument.id);
                    // Effect will update slides automatically
                  }
                }
              }
            }
          }}
          controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
          styles={{
            container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
            slide: {
              width: '90vw',
              height: '100vh'
            }
          }}
        />
      </Box>
    </>
  );
};

export default JobDetailPage;
